import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "../api";
import { AgentCardChart } from "../components/AgentCardChart";
import { agentKeys } from "../queryKeys";

interface Agent {
  id: string;
  name: string;
  display_name: string;
  domain: string;
  created_at: string;
}

export function DashboardPage() {
  const queryClient = useQueryClient();

  const { data: agents = [], isLoading: loading, error: queryError } = useQuery({
    queryKey: agentKeys.list(),
    queryFn: () => apiFetch<{ agents: Agent[] }>("/api/agents").then((r) => r.agents),
  });

  const createMutation = useMutation({
    mutationFn: () =>
      apiFetch<{ agent: Agent }>("/api/agents", {
        method: "POST",
        body: JSON.stringify({ name: "New Agent" }),
      }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: agentKeys.list() });
      window.location.href = `/agents/${data.agent.id}/general`;
    },
  });

  const error =
    (queryError as Error)?.message ?? (createMutation.error as Error)?.message ?? "";
  const creating = createMutation.isPending;

  function handleCreate() {
    createMutation.mutate();
  }

  return (
    <div className="dashboard">
      <header className="dash-header">
        <h1>Your Agents</h1>
        <div className="dash-header-actions">
          <button
            className="btn btn-primary"
            onClick={handleCreate}
            disabled={creating}
          >
            {creating ? "Creating..." : "+ New Agent"}
          </button>
        </div>
      </header>

      {error && <div className="auth-error">{error}</div>}

      {loading ? (
        <p className="dash-empty">Loading...</p>
      ) : agents.length === 0 ? (
        <div className="dash-empty">
          <p>No agents yet. Create your first one to get started.</p>
        </div>
      ) : (
        <div className="agent-grid">
          {agents.map(agent => (
            <a
              key={agent.id}
              href={`/agents/${agent.id}/general`}
              className="agent-card"
            >
              <h3>{agent.display_name || agent.name}</h3>
              <span className="agent-domain">
                {agent.domain || "No domain set"}
              </span>
              <div className="agent-card-chart-wrap">
                <AgentCardChart agentId={agent.id} />
              </div>
              <span className="agent-date">
                Created {new Date(agent.created_at).toLocaleDateString()}
              </span>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
